/**
 * Custom toast hook with theme-consistent colors
 *
 * Uses the app's custom green (#329239) and red (#c62828) colors
 * instead of Chakra's default bright colors.
 */

import { useToast, useColorMode, Box, HStack, Text, CloseButton } from '@chakra-ui/react';
import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// Theme colors for toasts
const TOAST_CONFIG = {
  success: {
    dark: { bg: '#1a4d1e', border: '#329239', icon: '#329239' },
    light: { bg: '#e8f5e9', border: '#329239', icon: '#329239' },
    icon: faCheckCircle
  },
  error: {
    dark: { bg: '#5c1a1a', border: '#c62828', icon: '#ef5350' },
    light: { bg: '#ffebee', border: '#c62828', icon: '#c62828' },
    icon: faExclamationCircle
  },
  warning: {
    dark: { bg: '#4d3800', border: '#f7931a', icon: '#f7931a' },
    light: { bg: '#fff8e1', border: '#f7931a', icon: '#f7931a' },
    icon: faExclamationTriangle
  },
  info: {
    dark: { bg: '#1a3a5c', border: '#4a96d4', icon: '#4a96d4' },
    light: { bg: '#e3f2fd', border: '#4a96d4', icon: '#4a96d4' },
    icon: faInfoCircle
  }
};

export const useThemedToast = () => {
  const toast = useToast();
  const { colorMode } = useColorMode();

  const themedToast = useCallback((options) => {
    const { title, description, status = 'info', duration = 3000, isClosable = true } = options;
    const config = TOAST_CONFIG[status] || TOAST_CONFIG.info;
    const colors = config[colorMode];

    return toast({
      duration,
      isClosable,
      position: 'bottom-right',
      render: ({ onClose }) => (
        <Box
          bg={colors.bg}
          borderWidth="1px"
          borderColor={colors.border}
          borderRadius="6px"
          p={3}
          boxShadow="lg"
          maxW="350px"
        >
          <HStack spacing={3} align="flex-start">
            <Box color={colors.icon} mt="2px">
              <FontAwesomeIcon icon={config.icon} />
            </Box>
            <Box flex={1}>
              {title && (
                <Text fontWeight="600" fontSize="sm" color={colorMode === 'dark' ? 'white' : 'gray.800'}>
                  {title}
                </Text>
              )}
              {description && (
                <Text fontSize="sm" color={colorMode === 'dark' ? 'white' : 'gray.600'} mt={title ? 1 : 0}>
                  {description}
                </Text>
              )}
            </Box>
            {isClosable && (
              <CloseButton size="sm" onClick={onClose} />
            )}
          </HStack>
        </Box>
      )
    });
  }, [toast, colorMode]);

  return themedToast;
};
